import styles from './ListItem.module.css';

import type { FC, ReactNode } from 'react';
import type { PayoutCurrency } from 'src/types/listing';

import React from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import { useNavigate } from 'react-router';

import { formatDay } from 'src/utils/date';
import { formatAmount } from 'src/utils/money';

import { Image } from 'src/components/themed/Image';
import { Stack } from 'src/components/themed/Stack';
import { Typography } from 'src/components/themed/Typography';
import { calculateSpacing } from 'src/components/themed/core/spacing';

import { Currency as FiatCurrency } from 'src/types/listing';

import { Currency } from '../Currency';

export interface ListItemProps {
  active?: boolean;
  completed?: boolean;
  title?: string;
  description?: string | null;
  amount?: number | string;
  stars?: number;
  currency?: PayoutCurrency;
  /** Fiat currency of `stars` — the listing's own; without it a euro price was badged as dollars. */
  fiatCurrency?: FiatCurrency;
  icon?: ReactNode;
  status?: ReactNode;
  image?: ReactNode;
  /**
   * Where the card leads. A card that leads somewhere IS a link: the whole row becomes the `<a>`,
   * so it has a name, an address, one Tab stop, a focus ring and a tap target the size of the
   * card — instead of a `div` with a click handler, which five screens' worth of cars could be
   * reached by mouse only and by nothing else (app#231, SF-J2, SF-I3).
   *
   * It replaced a bare `onClick` deliberately: a destination is now the only way to make a card
   * clickable, so "clickable but unreachable" can no longer be written.
   */
  to?: string;
  className?: string;
  date?: Date;
  children?: ReactNode;
  rightSection?: ReactNode;
}

export const ListItem: FC<ListItemProps> = (props) => {
  const {
    title,
    description,
    active,
    completed,
    icon,
    status,
    image,
    className,
    to,
    amount,
    stars,
    currency,
    fiatCurrency,
    children,
    rightSection,
    date,
  } = props;
  const navigate = useNavigate();

  /**
   * The card is an `<a href>`, so the browser's own link behaviour stays: a middle click or a
   * Cmd/Ctrl click opens the car in a new tab, the address shows on hover, Enter activates it.
   * A plain left click is ours to keep client-side — the same navigation the row always did.
   */
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!to) return;
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }

    event.preventDefault();
    navigate(to);
  };

  return (
    <CardBox
      as={to ? 'a' : 'div'}
      href={to}
      className={classNames(
        styles.container,
        active && styles.activeRewardItem,
        completed && styles.completedRewardItem,
        to && styles.clickable,
        className
      )}
      onClick={to ? handleClick : undefined}
    >
      {icon ? <Image size="small" icon={icon} /> : null}
      {image ? image : null}
      <Stack gap={0.5} flex={1} pl={2} position="relative">
        <Stack
          flexDirection="row"
          gap={1}
          alignItems="flex-start"
          justifyContent="space-between"
          flexWrap="nowrap"
        >
          <Typography fontWeight="600" color="white" flex={1}>
            {title}
          </Typography>
          {status}
        </Stack>
        {description ? (
          <Typography variant="medium" color="lightWhite">
            {description}
          </Typography>
        ) : null}
        {children}
      </Stack>
      <Stack flexDirection="row" gap={1} alignItems="center">
        {date ? (
          <Typography color="lightWhite" variant="small">
            {formatDay(date)}
          </Typography>
        ) : null}
        {amount ? (
          <div className={styles.amountWrapper}>
            <Typography variant="medium" fontWeight="500" color="white">
              {/* One formatter for both wire shapes. The old string branch grouped every third
                  digit of the whole string, so 21149.425287356324 read "21,149.425,287,356,324";
                  a non-numeric string ("Preparing...") still renders as the text it is. */}
              {typeof amount === 'number'
                ? formatAmount(amount)
                : Number.isFinite(Number(amount))
                  ? formatAmount(Number(amount), 6)
                  : amount}
            </Typography>
            <Currency currency={currency} />
          </div>
        ) : null}
        {stars && amount ? <div className={styles.divider} /> : null}
        {stars ? (
          <div className={styles.amountWrapper}>
            <Typography variant="medium" fontWeight="500" color="white">
              {formatAmount(stars)}
            </Typography>
            {/* A card that says nothing about its currency is a dollar card, as it always was —
                but a listing that names EUR is badged in EUR. */}
            <Currency fiat={fiatCurrency ?? FiatCurrency.USD} />
          </div>
        ) : null}
        {rightSection}
      </Stack>
    </CardBox>
  );
};

/**
 * The card's own frame. It is not `Stack` because it has to be able to render as an `<a>`: a
 * styled component built on a DOM tag drops the props that are not HTML attributes, while one
 * given a component target forwards every one of them — `gap`, `ph`, `flexDirection` — onto the
 * anchor, where React prints a warning per prop and per card.
 */
const CardBox = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: ${calculateSpacing(2)}px;
  padding: ${calculateSpacing(2)}px;
  /* An anchor brings these along; the card is not a run of text. */
  color: inherit;
  text-decoration: none;
`;
